import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { convertNumberToWords } from "./utility";

/**
 * Modern A4 document generator (jsPDF)
 * Layout inspired by pdfMake implementation
 */
export const generateReceptionLikePdfMake = (
    note,
    {
        docType = "BON DE RÉCEPTION",
        showPrices = true,
        priceMode = "TTC", // "HT" | "TTC"
        company = {},
    } = {}
) => {

    const doc = new jsPDF({ unit: "mm", format: "a4" });

    /* ================== CONSTANTS ================== */
    const PAGE_W = doc.internal.pageSize.width;
    const PAGE_H = doc.internal.pageSize.height;
    const M = 20;
    const FOOTER_H = 22;

    const BRAND = {
        primary: [6, 145, 43],
        ink: [15, 23, 42],
        subtle: [100, 116, 139],
        line: [226, 232, 240],
        headerBg: [241, 245, 249],
    };

    /* ================== HELPERS ================== */
    const fmtMoney = (v) =>
        Number(v || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ");

    const frDate = (d) =>
        d ? new Date(d).toLocaleDateString("fr-FR") : "-";

    const amountToWords = (n) =>
        convertNumberToWords(Math.round(n || 0)).toUpperCase() + " DH";

    /* ================== HEADER ================== */
    const drawHeader = () => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(22);
        doc.setTextColor(...BRAND.primary);
        doc.text(company.name || "MA SOCIÉTÉ", PAGE_W / 2, 22, { align: "center" });

        if (company.subtitle) {
            doc.setFontSize(10);
            doc.text(company.subtitle, PAGE_W / 2, 29, { align: "center" });
        }

        doc.setDrawColor(...BRAND.line);
        doc.line(M, 34, PAGE_W - M, 34);
    };

    /* ================== INFO CARDS ================== */
    const CARD_W = (PAGE_W - M * 3) / 2;
    const CARD_H = 45;
    const CARD_Y = 40;

    const drawCard = (x, title, lines) => {
        doc.setDrawColor(0);
        doc.roundedRect(x, CARD_Y, CARD_W, CARD_H, 3, 3);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(...BRAND.ink);
        doc.text(String(title || ""), x + 5, CARD_Y + 8);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(...BRAND.subtle);

        (lines || []).forEach((l, i) => {
            if (l === null || l === undefined) return;

            // ✅ jsPDF-safe conversion
            const text =
                typeof l === "string" ? l :
                    typeof l === "number" ? String(l) :
                        JSON.stringify(l);

            doc.text(text, x + 5, CARD_Y + 16 + i * 6);
        });
    };

    /* ================== TABLE ================== */
    const tableY = CARD_Y + CARD_H + 10;

    const headers = showPrices
        ? ["#", "Article", "Qté", "UM", priceMode === "HT" ? "PU HT" : "PU TTC", priceMode === "HT" ? "Total HT" : "Total TTC"]
        : ["#", "Article", "Qté", "UM"];

    const rows = (note.lignes || []).map((l, i) =>
        showPrices
            ? [
                i + 1,
                l.articleNom,
                l.quantiteRecue,
                l.unite,
                fmtMoney(priceMode === "HT" ? l.prixUnitaireHT : l.prixUnitaireTTC),
                fmtMoney(priceMode === "HT" ? l.montantHT : l.montantTTC),
            ]
            : [i + 1, l.articleNom, l.quantiteRecue, l.unite]
    );

    /* ================== TOTALS ================== */
    const drawTotals = (y) => {
        const boxW = 70;
        const x = PAGE_W - M - boxW;

        doc.setFillColor(163, 240, 183);
        doc.roundedRect(x, y, boxW, 24, 3, 3, "F");

        doc.setFont("helvetica", "bold");
        doc.setTextColor(...BRAND.ink);

        doc.text("TOTAL TTC", x + 5, y + 10);
        doc.text(fmtMoney(note.montantTotalTTC), x + boxW - 5, y + 18, {
            align: "right",
        });
    };

    /* ================== FOOTER ================== */
    const drawFooter = (pageNo) => {
        doc.setDrawColor(0);
        doc.line(M, PAGE_H - FOOTER_H, PAGE_W - M, PAGE_H - FOOTER_H);

        doc.setFontSize(8);
        doc.setTextColor(80);
        doc.text(
            `${company.address || ""} • ${company.phone1 || ""} • ${company.email || ""}`,
            PAGE_W / 2,
            PAGE_H - 12,
            { align: "center" }
        );

        doc.text(
            `Page ${pageNo}`,
            PAGE_W - M,
            PAGE_H - 6,
            { align: "right" }
        );
    };

    const paymentLabel = (mode) => {
        switch (mode) {
            case "CARTE_BANCAIRE":
                return "Carte bancaire";
            case "ESPECES":
                return "Espèces";
            case "CHEQUE":
                return "Chèque";
            case "VIREMENT":
                return "Virement";
            case "TRAITE":
                return "Traite";
            default:
                return mode || "-";
        }
    };
    /* ================== BUILD DOCUMENT ================== */
    drawHeader();

    const chequeSuffix =
        note.modeReglement?.toUpperCase() === "CHEQUE" && note.numeroCheque
            ? ` (N° chèque : ${note.numeroCheque})`
            : "";

    drawCard(M, docType, [
        `Numéro : ${note.numero || "-"}`,
        `Date Réception : ${note.dateReception || "-"}`,
        `BC N° : ${note.commandeNumero || "N/A"}    /    BL N° : ${note.deliveryNoteNb || "N/A"}`,
        `Transporteur : ${note.transporteurName || "-"} (${note.transporteurMatricule || "-"})`,
        `Mode paiement : ${paymentLabel(note.modeReglement)}${chequeSuffix}`,
    ]);


    drawCard(
        M * 2 + CARD_W,
        "FOURNISSEUR",
        [
            note.fournisseurNom,
            note.fournisseurVille,
            `Tél : ${note.fournisseurTelephone || "-"}`,
        ]
    );

    autoTable(doc, {
        startY: tableY,
        head: [headers],
        body: rows,
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: {
            fillColor: BRAND.headerBg,
            textColor: BRAND.ink,
            fontStyle: "bold",
        },
        didDrawPage: (data) => {
            drawFooter(doc.internal.getNumberOfPages());
        },
    });

    let yAfter = doc.lastAutoTable.finalY + 10;




    if (showPrices) {
        drawTotals(yAfter);
        doc.setFontSize(9);
        doc.setTextColor(...BRAND.subtle);
        doc.text(
            `Arrêté le présent document à la somme de : ${amountToWords(note.montantTotalTTC)}`,
            M,
            yAfter + 30
        );

    }

    const pdfBlob = doc.output("bloburl");
    window.open(pdfBlob, "_blank");
};
